/**
 * A custom element that manages the free samples selector in the cart.
 *
 * Features:
 * - Opens an overlay view to select up to 2 free samples
 * - Handles search/filtering of samples
 * - Adds selected samples to cart
 * - Updates UI based on selection state
 */
class CartSamplesSelector extends HTMLElement {
  constructor() {
    super();

    this.selectedSamples = new Set();
    this.maxSelection = 2;
    this.sampleItems = [];
    this.refs = {};
    this.isToggling = false;
    this.isClearing = false; // Flag pour bloquer pendant le nettoyage

    // Bind methods for event listeners
    this.boundClickHandler = this.handleClick.bind(this);
    this.boundInputHandler = this.handleInput.bind(this);
    this.boundKeydownHandler = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    // Collect references to DOM elements (scoped to samples page)
    this.refs.samplesPage = document.getElementById('cart-samples-page');
    
    if (this.refs.samplesPage) {
      this.refs.searchInput = this.refs.samplesPage.querySelector('[ref="searchInput"]');
      this.refs.samplesList = this.refs.samplesPage.querySelector('[ref="samplesList"]');
      this.refs.subtitle = this.refs.samplesPage.querySelector('[ref="subtitle"]');
      this.refs.subtitleText = this.refs.samplesPage.querySelector('[ref="subtitleText"]');
      this.refs.addButton = this.refs.samplesPage.querySelector('[ref="addButton"]');
      
      // Collect sample items (scoped to samples page)
      this.sampleItems = Array.from(this.refs.samplesPage.querySelectorAll('.cart-sample-item'));
    }

    // Setup event listeners using the on:click pattern
    this.setupEventListeners();

    // Load currently selected samples
    this.loadSelectedSamples();

    // Update UI
    this.updateUI();
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.cartDrawer) {
      this.cartDrawer.removeEventListener('click', this.boundClickHandler);
      this.cartDrawer.removeEventListener('input', this.boundInputHandler);
      this.cartDrawer.removeEventListener('keydown', this.boundKeydownHandler);
    }
  }

  setupEventListeners() {
    // Trouver le drawer et stocker la référence
    this.cartDrawer = document.querySelector('.cart-drawer__dialog');
    
    if (!this.cartDrawer) {
      console.warn('cart-drawer__dialog not found, event listeners not attached');
      return;
    }

    // Attacher les listeners (seront nettoyés dans disconnectedCallback)
    this.cartDrawer.addEventListener('click', this.boundClickHandler);
    this.cartDrawer.addEventListener('input', this.boundInputHandler);
    this.cartDrawer.addEventListener('keydown', this.boundKeydownHandler);
  }

  handleClick(e) {
    const target = e.target.closest('[on\\3a click]');
    if (!target) return;

    const handler = target.getAttribute('on:click');
    
    // Vérifier que c'est un handler cart-samples-selector
    if (!handler?.startsWith('cart-samples-selector/')) return;

    e.preventDefault();

    if (handler === 'cart-samples-selector/open') {
      this.open();
    } else if (handler === 'cart-samples-selector/close') {
      this.close();
    } else if (handler === 'cart-samples-selector/toggleSample') {
      this.toggleSample({
        currentTarget: target,
        target: e.target,
        preventDefault: () => {},
      });
    } else if (handler === 'cart-samples-selector/addToCart') {
      this.addToCart();
    }
  }

  handleInput(e) {
    const target = e.target.closest('[on\\3a input]');
    if (!target) return;

    const handler = target.getAttribute('on:input');
    
    if (handler !== 'cart-samples-selector/search') return;

    this.search(e);
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      const samplesPage = document.getElementById('cart-samples-page');
      if (samplesPage && !samplesPage.hidden) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    }
  }

  /**
   * Opens the samples page
   */
  async open() {
    const cartItemsComponent = document.querySelector('.cart-items-component');
    const samplesPage = document.getElementById('cart-samples-page');

    if (!samplesPage) {
      console.warn('cart-samples-page not found');
      return;
    }

    // Vérifier si le panier qualifie toujours pour les échantillons
    const cartQualifies = samplesPage.dataset.cartQualifies === 'true';
    if (!cartQualifies) {
      console.warn('Cart does not meet minimum requirement for free samples');
      return;
    }

    // ÉTAPE 1 : Lire le panier pour voir combien d'échantillons existent
    this.isClearing = true;
    const cart = await (await fetch('/cart.js')).json();
    const existingSamples = cart.items
      .filter(item => item.properties?._free_sample === 'true')
      .map(item => String(item.variant_id));

    // Si < 2 échantillons → Garder et pré-cocher
    if (existingSamples.length < 2) {
      this.selectedSamples = new Set(existingSamples);
    } else {
      // Si déjà 2 → Vider tout et recommencer
      await this.clearAllFreeSamples();
    }

    this.isClearing = false;
    this.updateUI();

    // ÉTAPE 2 : Masquer et désactiver le contenu principal du panier
    if (cartItemsComponent) {
      cartItemsComponent.style.display = 'none';
      cartItemsComponent.setAttribute('inert', '');
    }

    // ÉTAPE 3 : Afficher la page d'échantillons
    samplesPage.hidden = false;
    samplesPage.removeAttribute('inert');

    // Focus on search input
    const searchInput = samplesPage.querySelector('[ref="searchInput"]');
    if (searchInput) {
      setTimeout(() => searchInput.focus(), 100);
    }
  }

  /**
   * Closes the samples page and refreshes the cart
   */
  async close() {
    const cartItemsComponent = document.querySelector('.cart-items-component');
    const samplesPage = document.getElementById('cart-samples-page');
    const backButton = samplesPage?.querySelector('.cart-samples-page__back-button');
    
    // Show loading state on back button
    if (backButton) {
      backButton.classList.add('is-loading');
      backButton.disabled = true;
    }
    
    try {
      // Rafraîchir le panier pour afficher les échantillons
      await this.refreshCart();
      
      // Masquer la page d'échantillons
      if (samplesPage) {
        samplesPage.hidden = true;
        samplesPage.setAttribute('inert', '');
      }
      
      // Réafficher et réactiver le contenu principal du panier
      if (cartItemsComponent) {
        cartItemsComponent.style.display = '';
        cartItemsComponent.removeAttribute('inert');
      }
    } finally {
      // Remove loading state
      if (backButton) {
        backButton.classList.remove('is-loading');
        backButton.disabled = false;
      }
    }
  }

  /**
   * Toggles sample selection
   * @param {Event} event - The click event
   */
  async toggleSample(event) {
    // Prevent race conditions with multiple rapid clicks or during clearing
    if (this.isToggling || this.isClearing) return;
    this.isToggling = true;

    const button = event.currentTarget;
    const variantId = button.getAttribute('data-variant-id');
    const sampleItem = button.closest('.cart-sample-item');

    if (!variantId || !sampleItem) {
      this.isToggling = false;
      return;
    }

    // Show loading state
    button.classList.add('is-loading');
    button.disabled = true;

    try {
      const isSelected = this.selectedSamples.has(variantId);

      if (isSelected) {
        // Deselect: remove from cart
        await this.removeSampleFromCart(variantId);
        this.selectedSamples.delete(variantId);
      } else {
        // Check if max selection reached
        if (this.selectedSamples.size >= this.maxSelection) {
          return;
        }

        // Select: add to cart
        await this.addSampleToCart(variantId);
        this.selectedSamples.add(variantId);
      }

      this.updateUI();
    } finally {
      // Remove loading state
      button.classList.remove('is-loading');
      button.disabled = false;
      this.isToggling = false;
    }
  }

  /**
   * Normalizes text for search: removes accents, lowercase, removes extra spaces
   * @param {string} text - The text to normalize
   * @returns {string} - Normalized text
   */
  normalizeForSearch(text) {
    if (!text) return '';
    
    return text
      .toLowerCase()
      .normalize('NFD') // Décompose les caractères accentués
      .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
      .replace(/\s+/g, ' ') // Normalise les espaces multiples
      .trim();
  }

  /**
   * Filters samples based on search input
   * @param {Event} event - The input event
   */
  search(event) {
    const input = event.target || event.currentTarget;
    const searchTerm = this.normalizeForSearch(input.value);

    // Si le terme de recherche est vide, afficher tous les éléments
    if (!searchTerm) {
      this.sampleItems.forEach((item) => {
        item.style.display = '';
      });
      return;
    }

    this.sampleItems.forEach((item) => {
      const title = item.getAttribute('data-sample-title') || '';
      const variantTitle = item.getAttribute('data-variant-title') || '';
      const family = item.getAttribute('data-family') || '';
      const familyDescription = item.getAttribute('data-family-description') || '';
      
      // Normaliser tous les champs de recherche
      const normalizedTitle = this.normalizeForSearch(title);
      const normalizedVariantTitle = this.normalizeForSearch(variantTitle);
      const normalizedFamily = this.normalizeForSearch(family);
      const normalizedFamilyDescription = this.normalizeForSearch(familyDescription);
      
      // Vérifier si le terme de recherche est trouvé dans un des champs
      const matches = normalizedTitle.includes(searchTerm) || 
                      normalizedVariantTitle.includes(searchTerm) ||
                      normalizedFamily.includes(searchTerm) ||
                      normalizedFamilyDescription.includes(searchTerm);
      
      item.style.display = matches ? '' : 'none';
    });
  }

  /**
   * Loads currently selected samples from cart
   */
  loadSelectedSamples() {
    // Get selected samples from cart via data attributes
    const selectedInCart = document.querySelectorAll('[data-free-sample="true"]');
    selectedInCart.forEach((item) => {
      const variantId = item.getAttribute('data-variant-id');
      if (variantId) {
        this.selectedSamples.add(variantId);
      }
    });
  }

  /**
   * Updates UI based on current selection state
   */
  updateUI() {
    const selectionCount = this.selectedSamples.size;

    // Update subtitle message based on selection count
    if (this.refs.subtitleText) {
      const locale = document.documentElement.lang || 'fr';
      const messages = {
        fr: {
          first: 'Étape 1 : Veuillez choisir un premier échantillon',
          second: 'Étape 2 : Veuillez choisir un deuxième échantillon'
        },
        en: {
          first: 'Step 1: Please choose your first sample',
          second: 'Step 2: Please choose your second sample'
        }
      };
      
      const currentMessages = messages[locale] || messages.fr;
      this.refs.subtitleText.textContent = selectionCount === 0 
        ? currentMessages.first 
        : currentMessages.second;
    }

    // Show/hide add to cart button and subtitle
    if (this.refs.addButton && this.refs.subtitle) {
      if (selectionCount >= this.maxSelection) {
        this.refs.addButton.hidden = false;
        this.refs.subtitle.hidden = true;
      } else {
        this.refs.addButton.hidden = true;
        this.refs.subtitle.hidden = false;
      }
    }

    // Update sample items UI
    this.sampleItems.forEach((item) => {
      const button = item.querySelector('.cart-sample-item__button');
      const variantId = button?.getAttribute('data-variant-id');

      if (!variantId) return;

      const isSelected = this.selectedSamples.has(variantId);
      const isDisabled = !isSelected && this.selectedSamples.size >= this.maxSelection;

      item.classList.toggle('is-selected', isSelected);
      item.classList.toggle('is-disabled', isDisabled);
    });
  }

  /**
   * Clears all free samples from the cart
   * Called when opening the samples selector to ensure a fresh start
   * Only removes items with _free_sample property, leaves regular products untouched
   * @returns {Promise<void>}
   */
  async clearAllFreeSamples() {
    try {
      // Get current cart
      const cart = await (await fetch('/cart.js')).json();

      // Find all free sample items (NOT regular perfumes)
      const freeSampleItems = cart.items.filter(item =>
        item.properties?._free_sample === 'true'
      );

      if (freeSampleItems.length === 0) return;

      // Remove all free samples in parallel (faster)
      await Promise.all(
        freeSampleItems.map(item =>
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          })
        )
      );

      // Clear selected samples set
      this.selectedSamples.clear();
      this.updateUI();
    } catch (error) {
      console.error('Error clearing free samples', error);
    }
  }

  /**
   * Adds a sample to the cart
   * @param {string} variantId - The variant ID to add
   * @returns {Promise<void>}
   */
  async addSampleToCart(variantId) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _free_sample: 'true',
              },
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add sample to cart');
      }

      // Note: cart:update will be dispatched by refreshCart() when closing
    } catch (error) {
      console.error('Error adding sample to cart', error);
    }
  }

  /**
   * Removes a sample from the cart
   * @param {string} variantId - The variant ID to remove
   * @returns {Promise<void>}
   */
  async removeSampleFromCart(variantId) {
    try {
      // First, get cart to find the line item key
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Find the line item for this variant with _free_sample property
      const lineItem = cart.items.find((item) => {
        return String(item.variant_id) === String(variantId) && item.properties._free_sample === 'true';
      });

      if (!lineItem) return;

      // Remove the item
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineItem.key,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove sample from cart');
      }

      // Note: cart:update will be dispatched by refreshCart() when closing
    } catch (error) {
      console.error('Error removing sample from cart', error);
    }
  }

  /**
   * Refreshes the cart by re-fetching the cart section
   */
  async refreshCart() {
    const cartItemsComponent = document.querySelector('.cart-items-component');
    if (!cartItemsComponent) return;

    const sectionId = cartItemsComponent.dataset.sectionId;
    if (!sectionId) return;

    try {
      // Fetch the current cart to get item count
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Fetch the updated cart section HTML
      const response = await fetch(`/?section_id=${sectionId}`);
      const html = await response.text();

      // Parse and extract the cart items component
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCartItems = doc.querySelector('.cart-items-component');

      if (newCartItems) {
        // Replace the old cart with the new one
        cartItemsComponent.innerHTML = newCartItems.innerHTML;
      }

      // Dispatch cart:update event to update cart icon and other components
      const event = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'cart-samples-selector',
          data: {
            itemCount: cart.item_count,
            source: 'cart-samples-selector'
          }
        }
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  /**
   * Confirms the selection and closes the samples selector
   * The samples are already added to cart via toggleSample
   */
  async addToCart() {
    const samplesPage = document.getElementById('cart-samples-page');
    const addButton = samplesPage?.querySelector('[ref="addButton"]');

    // Show loading state on button
    if (addButton) {
      const loadingText = addButton.dataset.textLoading || 'Loading...';
      addButton.disabled = true;
      addButton.textContent = loadingText;
    }

    // Wait a bit for cart to update
    await new Promise(resolve => setTimeout(resolve, 300));

    // Redirect to checkout
    window.location.href = '/checkout';
  }
}

if (!customElements.get('cart-samples-selector')) {
  customElements.define('cart-samples-selector', CartSamplesSelector);
}
